import React, { useState, useEffect } from "react";
import axios from "axios";
import { orderBy } from "@progress/kendo-data-query";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import ApiUrls from "../../helper/api-urls";
import ApiHelper from "../../helper/api-helper";
import { NotificationManager } from "src/util/notify";
import Loader from "../../control-components/loader/loader";

const NO_CACHE = "no-cache, no-store, must-revalidate";
const EXPIRES_HTTP_DATE = "Thu, 01 Jan 1970 00:00:00 GMT";

interface UploadUrlResponse {
    uploadUrl: string;
    fileKey: string;
    jobId: string;
}

interface S3File {
    fileName: string;
    url: string;
    lastModified?: Date;
    size?: number;
}

interface SelectedFileWithJobId {
    file: File;
    jobId: string;
}

const DocumentUploader = () => {
    const [selectedFiles, setSelectedFiles] = useState<SelectedFileWithJobId[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [successMessage, setSuccessMessage] = useState("");
    const [uploadedJobs, setUploadedJobs] = useState<string[]>([]);
    const [s3Files, setS3Files] = useState<S3File[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sort, setSort] = useState<any[]>([]);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        fetchS3Files();
    }, []);

    const fetchS3Files = async () => {
        setLoadingFiles(true);
        try {
            const result = await ApiHelper.getRequest(ApiUrls.GET_ALL_AI_DOCUMENT_FILES);
            if (result?.resultData?.files) {
                const files: S3File[] = result.resultData.files
                    .map((file: any) => {
                        try {
                            if (!file || typeof file !== 'object') {
                                console.warn('Invalid file object encountered:', file);
                                return null;
                            }

                            return {
                                fileName: file.fileName || 'Unknown',
                                url: file.url || '',
                                lastModified: file.lastModified ? new Date(file.lastModified) : new Date(),
                                size: file.size || 0
                            };
                        } catch (error) {
                            console.error('Failed to process file:', file, error);
                            return null;
                        }
                    })
                    .filter((file): file is S3File => file !== null);

                setS3Files(files);
            }
        } catch (error) {
            console.error("Failed to fetch S3 files:", error);
            NotificationManager.error("Failed to load files from S3");
        } finally {
            setLoadingFiles(false);
        }
    };

    const pageChange = (event: any) => {
        setPage(event.page.skip);
        setPageSize(event.page.take);
    };

    const handleFilter = (e: any) => {
        setSearchText(e.target.value);
        setPage(0);
    };

    const getFilteredFiles = () => {
        if (!searchText) return s3Files;
        return s3Files.filter((file) =>
            file.fileName?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    const formatDate = (date?: Date) => {
        if (!date) return "N/A";
        const d = new Date(date);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
    };

    const formatFileSize = (size?: number) => {
        if (!size) return "N/A";
        return `${(size / 1024).toFixed(2)} KB`;
    };

    const handleFileSelection = (files: File[]) => {
        const newFiles = files.map(file => ({
            file,
            jobId: file.name.replace(/\.[^/.]+$/, "")
        }));
        setSelectedFiles((prev) => [...prev, ...newFiles]);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        handleFileSelection(Array.from(event.dataTransfer.files));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            handleFileSelection(Array.from(event.target.files));
        }
    };

    const triggerLambda = async (jobId: string, bucket: string, key: string): Promise<boolean> => {
        try {
            const result = await ApiHelper.postRequest(ApiUrls.TRIGGER_LAMBDA_PROCESSING, { jobId, bucket, key });
            return result?.status || false;
        } catch (error) {
            console.error("Lambda trigger error:", error);
            NotificationManager.error(`Failed to trigger processing for ${jobId}`);
            return false;
        }
    };

    const getPresignedUrl = async (file: File): Promise<UploadUrlResponse | null> => {
        try {
            const postData = {
                fileName: file.name,
                fileType: file.type,
                expires: EXPIRES_HTTP_DATE,
                cacheControl: NO_CACHE,
            };

            const result = await ApiHelper.postRequest(ApiUrls.GET_AI_DOC_TEMPLATE_MOCKUP_SIGNED_URL, postData);
            return result?.resultData || null;
        } catch (error) {
            NotificationManager.error("Failed to get S3 upload URL");
            return null;
        }
    };

    const uploadToS3 = async (uploadUrl: string, file: File, onProgress: (progress: number) => void): Promise<void> => {
        await axios.put(uploadUrl, file, {
            headers: {
                "Content-Type": file.type,
                "Expires": EXPIRES_HTTP_DATE,
                "Cache-Control": NO_CACHE,
            },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    onProgress(percent);
                }
            },
        });
    };

    const uploadFiles = async () => {
        if (selectedFiles.length === 0) return;

        const missingJobIds = selectedFiles.filter(sf => !sf.jobId.trim());
        if (missingJobIds.length > 0) {
            NotificationManager.error("Please enter JSX File Name for all files");
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setSuccessMessage("");
        setUploadedJobs([]);

        let completed = 0;
        const jobIds: string[] = [];
        const bucket = "dev.ai.document.template.files";

        for (let i = 0; i < selectedFiles.length; i++) {
            const { file, jobId: userJobId } = selectedFiles[i];

            const presigned = await getPresignedUrl(file);
            if (!presigned) continue;

            try {
                await uploadToS3(presigned.uploadUrl, file, (progress) => {
                    const totalProgress = Math.round((completed * 100 + progress) / selectedFiles.length);
                    setUploadProgress(totalProgress);
                });

                const lambdaTriggered = await triggerLambda(userJobId, bucket, file.name);

                if (lambdaTriggered) {
                    jobIds.push(userJobId);
                } else {
                    console.warn(`Lambda trigger failed for ${file.name}`);
                }

                completed++;
            } catch (error) {
                console.error("Upload error:", error);
                NotificationManager.error(`Failed to upload ${file.name}`);
            }
        }

        setUploading(false);
        setSelectedFiles([]);
        setUploadedJobs(jobIds);
        setSuccessMessage("Files uploaded and processing started!");
        fetchS3Files();
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const updateJobId = (index: number, newJobId: string) => {
        setSelectedFiles((prev) => 
            prev.map((sf, i) => i === index ? { ...sf, jobId: newJobId } : sf)
        );
    };

    const clearAllFiles = () => {
        setSelectedFiles([]);
    };

    const handleDeleteFile = async (fileName: string, fileUrl: string) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${fileName}"?\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) return;

        try {
            const urlObj = new URL(fileUrl);
            const fileKey = decodeURIComponent(urlObj.pathname.substring(1));

            const result = await ApiHelper.deleteRequest(
                `${ApiUrls.DELETE_AI_DOC_TEMPLATE_FILE}?fileKey=${encodeURIComponent(fileKey)}`
            );

            if (result?.status) {
                NotificationManager.success(`"${fileName}" deleted successfully`);
                fetchS3Files();
            } else {
                NotificationManager.error("Failed to delete file");
            }
        } catch (error) {
            console.error("Delete file error:", error);
            NotificationManager.error("An error occurred while deleting the file");
        }
    };

    const filteredFiles = getFilteredFiles();

    return (
        <div>
            <div className="text-center mb-4 py-3" style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <h3 className="fw-bold text-primary mb-0">
                    📄 AI Document Template Manager
                </h3>
            </div>

            <div className="row" style={{ marginTop: "20px" }}>
                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-primary text-white py-2">
                            <h5 className="mb-0" style={{ fontSize: "15px" }}>
                                <span className="me-2">📤</span>Upload Documents
                            </h5>
                        </div>
                        <div className="card-body p-3">
                            <div
                                className="drop-zone border-2 border-dashed rounded-3 p-4 text-center mb-3"
                                style={{ 
                                    cursor: "pointer",
                                    borderColor: "#0d6efd",
                                    backgroundColor: "#f8f9fa",
                                    transition: "all 0.3s ease",
                                    minHeight: "180px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center"
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.backgroundColor = "#e7f1ff";
                                }}
                                onDragLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                                }}
                                onDrop={(e) => {
                                    handleDrop(e);
                                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                                }}
                                onClick={() => document.getElementById("fileInput")?.click()}
                            >
                                <div className="mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="text-primary" viewBox="0 0 16 16">
                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"/>
                                    </svg>
                                </div>
                                <h6 className="text-primary mb-1" style={{ fontSize: "14px" }}>Drag & Drop Files Here</h6>
                                <p className="text-muted mb-1" style={{ fontSize: "12px" }}>or click to browse</p>
                                <small className="text-muted" style={{ fontSize: "11px" }}>
                                    PDF, PNG, JPG, JPEG, DOC, DOCX
                                </small>
                                <input
                                    type="file"
                                    id="fileInput"
                                    multiple
                                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                    className="d-none"
                                    onChange={handleFileChange}
                                />
                            </div>

                            {selectedFiles.length > 0 ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-secondary fw-bold" style={{ fontSize: "12px" }}>
                                            Selected Files ({selectedFiles.length})
                                        </small>
                                        <button 
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={clearAllFiles}
                                            type="button"
                                            style={{ fontSize: "11px", padding: "0.25rem 0.5rem" }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        {selectedFiles.map((selectedFile, index) => (
                                            <div 
                                                key={index} 
                                                className="p-2 mb-2 bg-light rounded border"
                                                style={{ fontSize: "12px" }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                                        <span className="me-1">
                                                            {selectedFile.file.type.includes('pdf') ? '📑' : 
                                                             selectedFile.file.type.includes('image') ? '🖼️' : '📝'}
                                                        </span>
                                                        <div style={{ minWidth: 0, flex: 1 }}>
                                                            <div className="text-truncate" title={selectedFile.file.name}>
                                                                {selectedFile.file.name}
                                                            </div>
                                                            <small className="text-muted" style={{ fontSize: "10px" }}>
                                                                {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        className="btn btn-sm btn-outline-danger ms-1"
                                                        onClick={() => removeFile(index)}
                                                        type="button"
                                                        style={{ fontSize: "10px", padding: "0.125rem 0.375rem" }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                                <div>
                                                    <label className="form-label mb-1" style={{ fontSize: "10px", fontWeight: "500" }}>
                                                        JSX File Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="e.g., EBTS_PsychEvaluationNote"
                                                        value={selectedFile.jobId}
                                                        onChange={(e) => updateJobId(index, e.target.value)}
                                                        style={{ fontSize: "11px", padding: "0.25rem 0.5rem" }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-4 text-muted" style={{ fontSize: "12px" }}>
                                    <div style={{ fontSize: "32px", opacity: 0.3 }}>📁</div>
                                    No files selected
                                </div>
                            )}

                            <div className="mt-3">
                                <button
                                    className="btn btn-primary w-100"
                                    disabled={selectedFiles.length === 0 || uploading}
                                    onClick={uploadFiles}
                                    style={{ fontSize: "13px", padding: "0.5rem" }}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Uploading {uploadProgress}%...
                                        </>
                                    ) : (
                                        <>
                                            <span className="me-2">🚀</span>
                                            Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>

                                {uploading && (
                                    <div className="progress mt-2" style={{ height: "6px" }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                            role="progressbar"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="alert alert-success mt-2 mb-0 py-2" role="alert" style={{ fontSize: "12px" }}>
                                        <div className="d-flex align-items-center">
                                            <span className="me-2">✅</span>
                                            <strong>{successMessage}</strong>
                                        </div>
                                        {uploadedJobs.length > 0 && (
                                            <div className="mt-2">
                                                <small className="d-block mb-1" style={{ fontSize: "11px" }}>Job IDs:</small>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {uploadedJobs.map((jobId, index) => (
                                                        <span key={index} className="badge bg-success" style={{ fontSize: "10px" }}>
                                                            {jobId}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-7">
                    <div className="d-flex flex-wrap justify-content-start align-items-center gap-2 mb-3">
                        <div className="col-lg-4 col-md-6 col-12" style={{ paddingLeft: "0px" }}>
                            <input
                                type="text"
                                value={searchText}
                                onChange={handleFilter}
                                name="searchContent"
                                className="form-control"
                                placeholder="Search files..."
                            />
                        </div>
                        <div className="d-flex align-items-end">
                            <button 
                                className="green-btn btn-primary" 
                                onClick={fetchS3Files}
                                disabled={loadingFiles}
                            >
                                <i className="fa fa-refresh" style={{ marginRight: '5px' }}></i>
                                {loadingFiles ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    <div className="grid-table filter-grid">
                        <div className="inner-section-edit position-relative text-center">
                            <div className="grid-table filter-grid header-blue">
                                {loadingFiles ? (
                                    <Loader />
                                ) : (
                                    <Grid
                                        scrollable="scrollable"
                                        data={orderBy(filteredFiles?.slice(page, pageSize + page), sort)}
                                        skip={page}
                                        take={pageSize}
                                        total={filteredFiles?.length}
                                        onPageChange={pageChange}
                                        pageable={{ pageSizes: [10, 20, 30] }}
                                        sort={sort}
                                        sortable={true}
                                        onSortChange={(e) => { setSort(e.sort); }}
                                    >
                                        <GridNoRecords>
                                            <div style={{ padding: "40px", textAlign: "center" }}>
                                                <div style={{ fontSize: "48px", opacity: 0.3 }}>📭</div>
                                                <h6 className="text-muted">No Files Found</h6>
                                                <p className="text-muted small">
                                                    {searchText ? `No files match "${searchText}"` : 'Upload some documents to get started'}
                                                </p>
                                            </div>
                                        </GridNoRecords>

                                        <GridColumn
                                            field="fileName"
                                            title="File Name"
                                            width="300px"
                                            cells={{
                                                data: (props) => {
                                                    let field = props?.dataItem;
                                                    return (
                                                        <td className="cursor-default">
                                                            <span className="me-2">
                                                                {field.fileName.endsWith('.jsx') ? '📜' : '📄'}
                                                            </span>
                                                            {field.fileName}
                                                        </td>
                                                    );
                                                }
                                            }}
                                        />

                                        <GridColumn
                                            field="size"
                                            title="Size"
                                            width="120px"
                                            cells={{
                                                data: (props) => {
                                                    let field = props?.dataItem;
                                                    return <td className="cursor-default">{formatFileSize(field.size)}</td>;
                                                }
                                            }}
                                        />

                                        <GridColumn
                                            field="lastModified"
                                            title="Last Modified"
                                            width="200px"
                                            cells={{
                                                data: (props) => {
                                                    let field = props?.dataItem;
                                                    return <td className="cursor-default">{formatDate(field.lastModified)}</td>;
                                                }
                                            }}
                                        />

                                        <GridColumn
                                            title="Action"
                                            width="200px"
                                            filterable={false}
                                            cells={{
                                                data: (props) => {
                                                    let field = props.dataItem;

                                                    const handleDownload = async (url: string, fileName: string) => {
                                                        try {
                                                            const response = await fetch(url);
                                                            const blob = await response.blob();
                                                            const downloadUrl = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = downloadUrl;
                                                            link.download = fileName;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(downloadUrl);
                                                        } catch (error) {
                                                            console.error('Download failed:', error);
                                                            NotificationManager.error('Failed to download file');
                                                        }
                                                    };

                                                    return (
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '100%' }}>
                                                                <div
                                                                    className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-blue"
                                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => window.open(field.url, '_blank')}
                                                                >
                                                                    <div className="k-chip-content ml-0">
                                                                        <Tooltip anchorElement="target" position="top">
                                                                            <i className="fa fa-eye" aria-hidden="true" title="View"></i>
                                                                        </Tooltip>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-green"
                                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                                                    onClick={() => handleDownload(field.url, field.fileName)}
                                                                >
                                                                    <div className="k-chip-content ml-0">
                                                                        <Tooltip anchorElement="target" position="top">
                                                                            <i className="fa fa-download" aria-hidden="true" title="Download"></i>
                                                                        </Tooltip>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-red"
                                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => handleDeleteFile(field.fileName, field.url)}
                                                                >
                                                                    <div className="k-chip-content ml-0">
                                                                        <Tooltip anchorElement="target" position="top">
                                                                            <i className="fa fa-trash" aria-hidden="true" title="Delete"></i>
                                                                        </Tooltip>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                            }}
                                        />
                                    </Grid>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentUploader;